import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { GetWindowDimensions } from "../../../hooks/window-hooks/WindowHooks";
import config from "../../../util/config";

// Define the keyframes style for the spinner animation
const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface FormData {
  firstName: string;
  lastName: string;
  gender: string;
  primaryNumber: string;
  secondaryNumber?: string;
  email: string;
  homeTown: string;
  stream: string;
  university: string;
  terms: boolean;
}

// Loader component for API processing
const Loader = () => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-5 rounded-lg flex flex-col items-center">
        <div className="w-12 h-12 rounded-full border-4 border-gray-200" style={{
          borderTopColor: '#4CAF50',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p className="text-gray-700 mt-3">Processing your registration...</p>
      </div>
    </div>
  );
};

const StudentRegistrationForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormData>();

  const primaryNumber = watch("primaryNumber");
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [duplicateRegistration, setDuplicateRegistration] = useState(false);
  const { width } = GetWindowDimensions();

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setDuplicateRegistration(false);
    try {
      // Execute the command using the Fetch API to simulate the curl request
      const response = await fetch(`${config.apiUrl}/api/scholarship-event/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          gender: data.gender,
          primaryNumber: data.primaryNumber,
          secondaryNumber: data.secondaryNumber || "",
          email: data.email,
          homeTown: data.homeTown,
          streamInterested: data.stream,
          interestedUniversities: data.university || "",
          termsAccepted: data.terms
        })
      });

      const responseData = await response.json();
      console.log("Scholarship event submission response:", responseData);

      // Check if the submission was successful
      if (responseData.status === "new") {
        // Set success state
        setSubmitSuccess(true);

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: "smooth" });
        reset();

        setTimeout(() => {
          window.location.href = "/events/college-mentor-scholarship";
        }, 5000);
      } else if (responseData.status === "duplicate") {
        // Handle duplicate registration case
        setDuplicateRegistration(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred while submitting the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isPrimaryNumberValid =
    primaryNumber && primaryNumber.length === 10 && !errors.primaryNumber;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-3">
      {/* Add the spinner animation style */}
      <style dangerouslySetInnerHTML={{ __html: spinnerStyle }} />
      
      {/* Success banner */}
      {submitSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">Success!</strong>
          <span className="block sm:inline">
            {" "}
            Registration completed successfully. We will contact you shortly.
          </span>
        </div>
      )}

      {/* Duplicate registration banner */}
      {duplicateRegistration && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">Already Registered!</strong>
          <span className="block sm:inline">
            {" "}
            A registration already exists for this mobile number.
          </span>
        </div>
      )}

      {loading && <Loader />}

      <input
        type="text"
        placeholder="First Name"
        {...register("firstName", { required: true })}
        className="border-[0.25px] p-2 rounded w-full border-solid border-[#A3A2AC] text-sm text-black focus:border-blue"
      />
      {errors.firstName && (
        <p className="text-red-500 text-sm">First name is required</p>
      )}

      <input
        type="text"
        placeholder="Last Name"
        {...register("lastName", { required: true })}
        className="border-[0.25px] p-2 rounded w-full border-solid border-[#A3A2AC] text-sm text-black focus:border-blue"
      />
      {errors.lastName && (
        <p className="text-red-500 text-sm">Last name is required</p>
      )}

      <div className="flex gap-2">
        <select
          {...register("gender", { required: true })}
          className="border p-2 rounded w-1/3 max-sm:w-[50%]"
        >
          <option value="" className="max-md:text-base">
            Select Gender
          </option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>

        <div className="relative flex items-center w-2/3 max-sm:w-[50%]">
          <input
            type="tel"
            placeholder="Primary Number"
            {...register("primaryNumber", {
              required: true,
              pattern: /^[0-9]{10}$/,
            })}
            className="border-[0.25px] p-2 rounded w-full border-solid border-[#A3A2AC] text-sm text-black focus:border-blue"
          />
        </div>
      </div>
      {errors.primaryNumber && (
        <p className="text-red-500 text-sm">
          Valid 10-digit mobile number required
        </p>
      )}

      <input
        type="tel"
        placeholder="Secondary Number"
        {...register("secondaryNumber")}
        className="border-[0.25px] p-2 rounded w-full border-solid border-[#A3A2AC] text-sm text-black focus:border-blue"
      />

      <input
        type="email"
        placeholder="Email"
        {...register("email", { required: true })}
        className="border-[0.25px] p-2 rounded w-full border-solid border-[#A3A2AC] text-sm text-black focus:border-blue"
      />
      {errors.email && (
        <p className="text-red-500 text-sm">Email is required</p>
      )}

      <input
        type="text"
        placeholder="Home Town"
        {...register("homeTown", { required: true })}
        className="border-[0.25px] p-2 rounded w-full border-solid border-[#A3A2AC] text-sm text-black focus:border-blue"
      />
      {errors.homeTown && (
        <p className="text-red-500 text-sm">Home town is required</p>
      )}

      <select
        {...register("stream", { required: true })}
        className="border-[0.25px] p-2 rounded w-full border-solid border-[#A3A2AC] text-sm text-black focus:border-blue"
      >
        <option value="" hidden>
          Stream Interested
        </option>
        <option value="agriculture">Agriculture</option>
        <option value="architecture">Architecture</option>
        <option value="Aviation">Aviation</option>
        <option value="Law">Law</option>
        <option value="Engineering">Engineering</option>
        <option
          value="Medical Sciences
"
        >
          Medical Sciences
        </option>
        <option
          value="Liberal Arts
"
        >
          Liberal Arts
        </option>
        <option
          value="Fashion Designing
"
        >
          Fashion Designing
        </option>
        <option value="Hotel Management">Hotel Management</option>
      </select>
      {errors.stream && <p className="text-red-500 text-sm">Select a stream</p>}

      <input
        type="text"
        placeholder="Interested Universities"
        {...register("university")}
        className="border-[0.25px] p-2 rounded w-full border-solid border-[#A3A2AC] text-sm text-black focus:border-blue"
      />

      <label className="flex justify-center text-center items-start text-sm text-gray-500">
        <input
          type="checkbox"
          {...register("terms", { required: true })}
          className="mr-1 mt-1"
        />
        By clicking on this I will abide by all the Terms and Conditions by the
        organizers
      </label>
      {errors.terms && (
        <p className="text-red-500 text-sm">Please agree to terms</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className={`py-2 rounded mt-2 ${loading ? "bg-gray-400" : "bg-green hover:bg-blue"} text-white flex justify-center items-center`}
      >
        {loading ? (
          <>
            <div className="w-5 h-5 rounded-full border-2 border-gray-200 mr-2" style={{
              borderTopColor: 'white',
              animation: 'spin 1s linear infinite'
            }}></div>
            Processing...
          </>
        ) : (
          "Submit Now"
        )}
      </button>
    </form>
  );
};

export default StudentRegistrationForm;
